import { Box, Tooltip } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import BoltOutlined from "@mui/icons-material/BoltOutlined";

import {
  getExpirationDaysForDocType,
  isHardDurationDocType,
  isExplicitExpirationDocType,
  isDualDateDocType,
} from "../../../../constants/documents/docTypeExpirationDurations";
import Txt from "../../../../components/typography/Txt";

// Blue "auto" pill (mirrors the Fees tab AutoBadge) — used for doc types whose
// validity window is a fixed/system value. Always sources the day count from
// the duration map so it shows even when the doc hasn't had
// `reportGuidelineExpirationDays` stamped onto it yet.
export const AutoDurationPill = ({ days, sx }) => (
  <Tooltip title="Auto-assigned validity window for this document type">
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 0.75,
        py: 0.35,
        borderRadius: 0.75,
        background: blue[50],
        color: blue[700],
        border: `1px solid ${blue[100]}`,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        ...sx,
      }}
    >
      <BoltOutlined sx={{ fontSize: 13 }} />
      {days} days
    </Box>
  </Tooltip>
);

// Read-only duration display shared by the main Documents table. Hard types
// render the auto pill (fixed window from the map); soft / manually-set types
// render the plain day count; nothing set renders an em dash.
const DocDurationCell = ({ doc }) => {
  const docType = doc?.docType;
  // Full-height centered wrapper so the pill / text sits vertically centered
  // within the grid cell regardless of row height.
  let content;
  if (isExplicitExpirationDocType(docType) || isDualDateDocType(docType)) {
    // Explicit-expiration (license/passport) and dual-date (statements) types
    // derive their window from the picked dates — duration isn't a
    // user-meaningful standalone value here.
    content = <Txt sx={{ fontSize: 11, color: grey[400] }}>N/A</Txt>;
  } else if (isHardDurationDocType(docType)) {
    content = <AutoDurationPill days={getExpirationDaysForDocType(docType)} />;
  } else {
    const value = doc?.reportGuidelineExpirationDays;
    content =
      value == null || value === "" ? (
        <Txt sx={{ fontSize: 11, color: grey[400] }}>—</Txt>
      ) : (
        <Txt sx={{ fontSize: 11, color: "#0f172a" }}>{value} days</Txt>
      );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      {content}
    </Box>
  );
};

export default DocDurationCell;
